// @ts-nocheck
import { useState, useEffect, useRef, createContext, useContext, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { setSentryUser, setSentryTenant, clearSentryUser } from '@/lib/sentry';
import { setGA4User, clearGA4User, getStoredUtmParams, clearUtmParams } from '@/lib/tracking';
import type { Database } from '@/integrations/supabase/types';
import { DEFAULT_INACTIVITY_MINUTES, fetchSecurityRequirements } from '@/hooks/useSecurityRequirements';

type TenantType = Database['public']['Enums']['tenant_type'];
type TenantRole = Database['public']['Enums']['tenant_role'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isPartnerUser: boolean;
  tenantId: string | null;
  tenantType: TenantType | null;
  tenantRole: TenantRole | null;
  isLoading: boolean;
  isDetailsLoaded: boolean;
  termsAccepted: boolean | null;
  needsTermsAcceptance: boolean;
  signOut: () => Promise<void>;
  getDefaultRedirectPath: () => string | null;
  setTermsAsAccepted: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantType, setTenantType] = useState<TenantType | null>(null);
  const [tenantRole, setTenantRole] = useState<TenantRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailsLoaded, setIsDetailsLoaded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
  const [isPartnerUser, setIsPartnerUser] = useState(false);

  const fetchUserDetails = async (userId: string) => {
    try {
      // Check if user is Platform Admin (is_platform_admin flag) or has admin role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role, is_platform_admin')
        .eq('user_id', userId)
        .maybeSingle();

      if (roleError) {
        logger.error('Error fetching user roles', roleError);
      }

      const hasSuperAdmin = !!(roleData as any)?.is_platform_admin;
      const hasAdminRole = roleData?.role === 'Owner' || roleData?.role === 'Admin';
      setIsSuperAdmin(hasSuperAdmin);
      setIsAdmin(hasAdminRole);

      // Fetch profile info (profiles is a VIEW joining users + organizations)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('tenant_id, tenant_role')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        logger.error('Error fetching profile', profileError);
      }

      if (profile) {
        const tid = profile.tenant_id?.toString() ?? null;
        setTenantId(tid);
        if (tid) setSentryTenant(tid);
        setTenantRole(profile.tenant_role as TenantRole);
        // In current schema, all organizations are CUSTOMER type
        setTenantType('CUSTOMER' as TenantType);
        
        // Check if this user's organization is a partner
        if (profile.tenant_id) {
          const { data: partnerData } = await supabase
            .from('partners')
            .select('id')
            .eq('organization_id', profile.tenant_id)
            .eq('status', 'ativo')
            .maybeSingle();
          
          setIsPartnerUser(!!partnerData);
        }
      }

      // Fetch terms acceptance status
      const { data: userDetails } = await supabase
        .from('users')
        .select('terms_accepted_at')
        .eq('user_id', userId)
        .maybeSingle();

      setTermsAccepted(!!userDetails?.terms_accepted_at);
      setIsDetailsLoaded(true);

      // GA4 Auth Bridge — vincula user_id à jornada GA4 (com UTM de aquisição)
      const tid = profile?.tenant_id?.toString() ?? undefined;
      const utm = getStoredUtmParams();
      setGA4User(userId, {
        tenant_id: tid,
        tenant_type: 'CUSTOMER',
        role: (profile?.tenant_role as string) || undefined,
        ...(utm && {
          acquisition_source: utm.utm_source,
          acquisition_medium: utm.utm_medium,
          acquisition_campaign: utm.utm_campaign,
        }),
      });
      // Limpa UTMs após vincular ao user_id (first-login attribution done)
      if (utm) clearUtmParams();
    } catch (error) {
      logger.error('Error in fetchUserDetails', error);
      setIsDetailsLoaded(true);
    }
  };

  // Calculate the default redirect path based on user type
  const getDefaultRedirectPath = useCallback((): string | null => {
    // Wait until details are loaded
    if (!isDetailsLoaded) {
      return null;
    }
    if (isSuperAdmin) {
      return '/admin';
    }
    // Partner users go to partner portal
    if (isPartnerUser) {
      return '/partner';
    }
    if (tenantType === 'PARTNER') {
      return '/admin';
    }
    if (tenantId) {
      return '/app';
    }
    // No tenant - pending activation
    return '/login?mode=no-tenant';
  }, [isDetailsLoaded, isSuperAdmin, isPartnerUser, tenantType, tenantId]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setSentryUser({ id: session.user.id, email: session.user.email });
          // Use setTimeout to avoid Supabase client deadlock
          setTimeout(() => {
            fetchUserDetails(session.user.id).finally(() => {
              setIsLoading(false);
            });
          }, 0);
        } else {
          clearSentryUser();
          clearGA4User();
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setTenantId(null);
          setTenantType(null);
          setTenantRole(null);
          setTermsAccepted(null);
          setIsDetailsLoaded(false);
          setIsPartnerUser(false);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Ensure we resolve tenant/role even if onAuthStateChange doesn't fire
        setTimeout(() => {
          fetchUserDetails(session.user.id).finally(() => {
            setIsLoading(false);
          });
        }, 0);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ==========================================
  // Auto-logout after N min of inactivity (fase 2b R2: inactivity_minutes da org; 30 enquanto carrega ou em erro)
  // ==========================================
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inactivityMinutes, setInactivityMinutes] = useState<number>(DEFAULT_INACTIVITY_MINUTES);
  const userIdForInactivity = user?.id ?? null;

  useEffect(() => {
    setInactivityMinutes(DEFAULT_INACTIVITY_MINUTES);
    if (!userIdForInactivity) return;
    let cancelled = false;
    fetchSecurityRequirements()
      .then((requirements) => {
        if (!cancelled) setInactivityMinutes(requirements.inactivityMinutes);
      })
      .catch((error) => {
        logger.error('Error fetching security requirements', error);
      });
    return () => {
      cancelled = true;
    };
  }, [userIdForInactivity]);

  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        logger.info(`Auto-logout: ${inactivityMinutes} min inactivity`);
        signOutRef.current();
      }, inactivityMinutes * 60 * 1000);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [user, inactivityMinutes]);

  const signOutRef = useRef<() => Promise<void>>(async () => {});

  const signOut = async () => {
    // Log logout action (best effort) BEFORE invalidating the session.
    // If we fire-and-forget, the token may already be revoked when the request reaches the edge function.
    const currentUserId = user?.id;
    if (currentUserId) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;

        if (accessToken) {
          const headers = { Authorization: `Bearer ${accessToken}` };
          // Don't block logout for long; just give the log a short chance to be delivered.
          await Promise.race([
            Promise.all([
              // Log to api_access_logs (existing)
              supabase.functions.invoke("log-user-action", {
                headers,
                body: {
                  action: "auth:logout",
                  metadata: { user_id: currentUserId },
                },
              }),
              // Log to user_events (new)
              supabase.functions.invoke("log-user-action", {
                headers,
                body: {
                  evento: "Logout",
                  metadata: { user_id: currentUserId },
                },
              }),
            ]),
            new Promise((resolve) => setTimeout(resolve, 500)),
          ]);
        }
      } catch {
        // ignore
      }
    }

    await supabase.auth.signOut();
    clearSentryUser();
    clearGA4User();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setTenantId(null);
    setTenantType(null);
    setTenantRole(null);
    setTermsAccepted(null);
    setIsDetailsLoaded(false);
    setIsPartnerUser(false);
  };

  // Keep signOutRef in sync for inactivity timer
  signOutRef.current = signOut;

  // Computed property for checking if user needs to accept terms
  const needsTermsAcceptance = termsAccepted === false && tenantId !== null && !isSuperAdmin;

  // Function to update termsAccepted state after user accepts terms
  const setTermsAsAccepted = useCallback(() => {
    setTermsAccepted(true);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isAdmin, 
      isSuperAdmin,
      isPartnerUser,
      tenantId,
      tenantType,
      tenantRole,
      isLoading,
      isDetailsLoaded,
      termsAccepted,
      needsTermsAcceptance,
      signOut,
      getDefaultRedirectPath,
      setTermsAsAccepted,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
